import * as FullStory from '@fullstory/browser';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { type VisualizationData } from '~components/business-owner/barry/chart/BarryChart';
import { BARRY_WEBSOCKET_MESSAGE_TYPES } from '~components/business-owner/barry/constants';

import { CustomEventName, useCustomEvent } from '~hooks/useCustomEvent';

import { type BarryIndividualConversation as BarryIndividualConversationData } from '~api/common/barry/barry';
import { useGetIndividualConversation } from '~api/common/barry/queries/useGetIndividualConversation';
import { useSendMessageToBarry } from '~api/common/barry/queries/useSendMessageToBarry';
import { FULL_STORY_BARRY_TRACKING_EVENTS } from '~constants/fullstory/events/barry';
import { UIModeEnum, useBarry } from '~contexts/BarryContext';
import { GlobalState } from '~reducers';
import { callErrorToast } from '~utils/toasts';

import styles from './BarryIndividualConversation.module.scss';

import { BarryConversationMessages } from './BarryConversationMessages';
import { BarryMessageInputForm } from './BarryMessageInputForm';
import { BarryNewConversationView } from './BarryNewConversationView';
import { type ChatItem } from './types/chatItem';
import { type WebsocketMessageDetail } from './types/websocketMessageDetail';
import {
	buildTempItem,
	isTemporaryMessageId,
	processBarryMessages,
	updateOrAppendAssistantStream,
} from './utils';

export const BarryIndividualConversation: React.FC = () => {
	const {
		selectedConversationId,
		uiMode,
		beginTransition,
		completeNewChat,
		hasHitDailyLimit,
		incrementMessageCount,
	} = useBarry();

	const { userId, email, business } = useSelector(
		(state: GlobalState) => state?.session,
	);

	const [messageInput, setMessageInput] = useState('');
	const [messages, setMessages] = useState<ChatItem[]>([]);

	const handleConversationLoaded = useCallback(
		(data: BarryIndividualConversationData) => {
			setMessages(processBarryMessages(data?.messages));
		},
		[],
	);

	const { refetch: refetchIndividualConversation } = useGetIndividualConversation(
		selectedConversationId ?? '',
		!!selectedConversationId,
		handleConversationLoaded,
	);

	const { sendMessage: onSendMessage } = useSendMessageToBarry();

	const { subscribe, unsubscribe } = useCustomEvent(
		CustomEventName.BARRY_CONVERSATION_UPDATED,
	);

	const handleIncomingMessage = useCallback(
		(event: CustomEvent<WebsocketMessageDetail>) => {
			const { detail } = event;

			const isTerminalEvent = [
				BARRY_WEBSOCKET_MESSAGE_TYPES.ASSISTANT_MESSAGE,
				BARRY_WEBSOCKET_MESSAGE_TYPES.ERROR,
			].includes(detail.type);

			if (
				!selectedConversationId &&
				uiMode === UIModeEnum.TRANSITION &&
				detail.conversationId &&
				isTerminalEvent
			) {
				completeNewChat(detail.conversationId);
			}

			const visualization = detail.data?.metadata?.visualization as
				| VisualizationData
				| undefined;

			switch (detail.type) {
				case BARRY_WEBSOCKET_MESSAGE_TYPES.ASSISTANT_MESSAGE_CHUNK: {
					const accumulated = (detail.data.accumulated as string) ?? '';
					setMessages((prev) =>
						updateOrAppendAssistantStream(prev, accumulated, visualization),
					);
					return;
				}
				case BARRY_WEBSOCKET_MESSAGE_TYPES.ASSISTANT_MESSAGE: {
					if (selectedConversationId) {
						refetchIndividualConversation();
					}
					return;
				}
				default:
					return;
			}
		},
		[refetchIndividualConversation, selectedConversationId, uiMode, completeNewChat],
	);

	useEffect(() => {
		subscribe(handleIncomingMessage);
		return () => unsubscribe(handleIncomingMessage);
	}, [handleIncomingMessage, subscribe, unsubscribe]);

	const isBarryBusy = messages.some((item) => isTemporaryMessageId(item.id));

	const submitMessageToBarry = useCallback(async () => {
		if (!messageInput.trim() || isBarryBusy) {
			return;
		}

		if (hasHitDailyLimit) {
			callErrorToast({
				content:
					'You have reached your daily message limit with Barry! Your limit will reset tomorrow.',
			});
			return;
		}

		const userTemp = buildTempItem('user', messageInput);
		const thinkingStatus = buildTempItem('status', 'Thinking...');

		if (!selectedConversationId) {
			setMessages([userTemp, thinkingStatus]);
			beginTransition();
		} else {
			setMessages((prev) => [...prev, userTemp, thinkingStatus]);
		}

		onSendMessage({
			message: messageInput,
			...(selectedConversationId && { conversationId: selectedConversationId }),
		});
		setMessageInput('');
		incrementMessageCount();

		FullStory.event(FULL_STORY_BARRY_TRACKING_EVENTS.barryMessageSent, {
			userId,
			email,
			business,
		});
	}, [
		messageInput,
		selectedConversationId,
		beginTransition,
		onSendMessage,
		userId,
		email,
		business,
		hasHitDailyLimit,
		incrementMessageCount,
		isBarryBusy,
	]);

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (e.key === 'Enter' && messageInput.length > 0 && !e.shiftKey) {
			e.preventDefault();
			submitMessageToBarry();
		}
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setMessageInput(event.target.value);
	};

	const messagesEndRef = React.useRef<HTMLDivElement>(null);
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	const isNewChat = uiMode === UIModeEnum.NEW;
	const isInConversation = [UIModeEnum.CONVERSATION, UIModeEnum.TRANSITION].includes(
		uiMode,
	);

	return (
		<div className={styles.chatContainer}>
			<div className={styles.messagesArea}>
				{isNewChat && (
					<BarryNewConversationView
						messageInput={messageInput}
						hasHitDailyLimit={hasHitDailyLimit}
						onInputChange={handleInputChange}
						onKeyDown={onKeyDown}
						onSubmit={submitMessageToBarry}
						onSuggestionSelect={setMessageInput}
					/>
				)}
				{isInConversation && (
					<BarryConversationMessages
						items={messages}
						messagesEndRef={messagesEndRef}
					/>
				)}
			</div>
			{isInConversation && (
				<BarryMessageInputForm
					messageInput={messageInput}
					hasHitDailyLimit={hasHitDailyLimit}
					isBarryBusy={isBarryBusy}
					onInputChange={handleInputChange}
					onKeyDown={onKeyDown}
					onSubmit={submitMessageToBarry}
				/>
			)}
		</div>
	);
};
